"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ExampleButton,
  NoticeList,
  SmallButton,
  StatementCard,
} from "@/components/tools/publishing-statements/kit";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { asSentence } from "@/lib/tool-kit/publishing/authors";
import {
  type FunderInput,
  type FunderRole,
  type FundingStyle,
  fundingSentence,
  looksAbbreviated,
  namedFunders,
  parseGrantNumbers,
} from "@/lib/tool-kit/publishing/funding";
import { plainParagraph } from "@/lib/tool-kit/publishing/rich-text";

/** English strings. A translated content file overrides these through `ui`. */
export const FUNDING_STATEMENT_GENERATOR_STRINGS = {
  fundedLabel: "Did this research receive funding?",
  fundedYes: "Yes",
  fundedNo: "No specific funding",
  funderPanel: "Funder {number}",
  nameLabel: "Funder's official name",
  namePlaceholder: "e.g. the National Science Foundation",
  nameHint:
    'As it should read in the sentence — "the National Science Foundation", not "NSF".',
  locationLabel: "Location (optional)",
  locationPlaceholder: "e.g. Seattle, WA",
  grantsLabel: "Grant numbers (separate with commas)",
  grantsPlaceholder: "e.g. R01 GM123456, R01 GM654321",
  recipientsLabel: "Awarded to (optional)",
  recipientsPlaceholder: "e.g. M.O. and P.R.",
  urlLabel: "Funder website (optional)",
  urlPlaceholder: "https://",
  addFunder: "Add funder",
  removeFunder: "Remove funder",
  removeFunderAria: "Remove funder {number}",
  styleLabel: "Format",
  styleElsevier: "Elsevier pattern",
  styleNarrative: "Narrative (PLOS style)",
  roleLabel: "Funder's role in the study",
  roleNone: "State that the funders had no role",
  roleDescribed: "Describe the funders' role",
  roleOmit: "Leave it out",
  roleTextLabel: "What the funders did",
  roleTextPlaceholder:
    "e.g. The funder reviewed the study design but had no role in data collection, analysis, or the decision to publish.",
  tryExample: "Try an example",
  exampleFunders: ["the Eastbrook Research Council", "the Halden Foundation"],
  exampleLocations: ["Eastbrook, UK", "Oslo, Norway"],
  exampleGrants: ["EBRC-2024-117, EBRC-2024-118", "HF-3391"],
  exampleRecipients: ["M.O. and P.R.", "D.R."],
  exampleUrls: ["https://eastbrook-research.example", "https://halden.example"],
  results: "Your statement",
  heading: "Funding",
  copy: "Copy statement",
  copied: "Copied!",
  prompt: 'Name at least one funder, or choose "No specific funding".',
  noticeAbbreviation:
    "“{name}” looks like an abbreviation. Write the funder's full official name — registries list full names, and submission systems match against them.",
  noticeNoGrant:
    "No grant number for: {names}. Add every award number — funders use them to track the publications they paid for.",
  noticeRecipients:
    "The Elsevier pattern has no place for recipients. Switch to the narrative format if your journal asks who received each award.",
  noticeRegistry:
    "Publishers send funder names and grant numbers to Crossref so the article is linked to its funding. Crossref's Open Funder Registry is being merged into ROR, which now identifies funders too — in both, use the funder's official name.",
  listSeparator: ", ",
  statementLocale: "en",
  grantNumber: "one: grant number|other: grant numbers",
  lead: "This work was supported by {funders}.",
  withLocation: "{name}, {location}",
  elsevierBracket: "[{label} {numbers}]",
  elsevierSeparator: "; ",
  elsevierFinalSeparator: "; and ",
  narrativeAward: "{label} {numbers}",
  narrativeAwardTo: "{label} {numbers} to {recipients}",
  narrativeTo: "awarded to {recipients}",
  narrativeDetails: " ({details})",
  detailSeparator: "; ",
  noFundingElsevier:
    "This research did not receive any specific grant from funding agencies in the public, commercial, or not-for-profit sectors.",
  noFundingNarrative:
    "The author(s) received no specific funding for this work.",
  roleNoneElsevier:
    "one: The funder had no role in study design; in the collection, analysis and interpretation of data; in the writing of the report; or in the decision to submit the article for publication.|other: The funders had no role in study design; in the collection, analysis and interpretation of data; in the writing of the report; or in the decision to submit the article for publication.",
  roleNoneNarrative:
    "one: The funder had no role in study design, data collection and analysis, decision to publish, or preparation of the manuscript.|other: The funders had no role in study design, data collection and analysis, decision to publish, or preparation of the manuscript.",
} as const;

type Strings = typeof FUNDING_STATEMENT_GENERATOR_STRINGS;

interface FunderRow extends FunderInput {
  id: number;
}

const MAX_FUNDERS = 20;

function emptyFunder(id: number): FunderRow {
  return { id, name: "", location: "", grants: "", recipients: "", url: "" };
}

function FunderFields({
  row,
  index,
  canRemove,
  strings,
  onChange,
  onRemove,
}: {
  row: FunderRow;
  index: number;
  canRemove: boolean;
  strings: Strings;
  onChange: (patch: Partial<FunderRow>) => void;
  onRemove: () => void;
}) {
  return (
    <Panel title={t(strings.funderPanel, { number: index + 1 })}>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.nameHint}
          id={`fst-name-${row.id}`}
          label={strings.nameLabel}
          onChange={(name) => onChange({ name })}
          placeholder={strings.namePlaceholder}
          value={row.name}
        />
        <TextField
          id={`fst-location-${row.id}`}
          label={strings.locationLabel}
          onChange={(location) => onChange({ location })}
          placeholder={strings.locationPlaceholder}
          value={row.location}
        />
        <TextField
          id={`fst-grants-${row.id}`}
          label={strings.grantsLabel}
          mono
          onChange={(grants) => onChange({ grants })}
          placeholder={strings.grantsPlaceholder}
          value={row.grants}
        />
        <TextField
          id={`fst-recipients-${row.id}`}
          label={strings.recipientsLabel}
          onChange={(recipients) => onChange({ recipients })}
          placeholder={strings.recipientsPlaceholder}
          value={row.recipients}
        />
        <TextField
          id={`fst-url-${row.id}`}
          label={strings.urlLabel}
          onChange={(url) => onChange({ url })}
          placeholder={strings.urlPlaceholder}
          type="url"
          value={row.url}
        />
      </div>
      {canRemove ? (
        <SmallButton
          ariaLabel={t(strings.removeFunderAria, { number: index + 1 })}
          label={strings.removeFunder}
          onClick={onRemove}
        />
      ) : null}
    </Panel>
  );
}

function noticesFor(
  funders: readonly FunderRow[],
  style: FundingStyle,
  strings: Strings
): string[] {
  const named = namedFunders(funders);
  const notes: string[] = [];
  for (const funder of named.filter((item) => looksAbbreviated(item.name))) {
    notes.push(t(strings.noticeAbbreviation, { name: funder.name.trim() }));
  }
  const missing = named.filter(
    (funder) => parseGrantNumbers(funder.grants).length === 0
  );
  if (missing.length > 0) {
    notes.push(
      t(strings.noticeNoGrant, {
        names: missing
          .map((funder) => funder.name.trim())
          .join(strings.listSeparator),
      })
    );
  }
  if (
    style === "elsevier" &&
    named.some((funder) => funder.recipients.trim() !== "")
  ) {
    notes.push(strings.noticeRecipients);
  }
  if (named.length > 0) {
    notes.push(strings.noticeRegistry);
  }
  return notes;
}

export default function FundingStatementGeneratorWidget() {
  const strings = useToolUi(FUNDING_STATEMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [funded, setFunded] = useState(true);
  const [funders, setFunders] = useState<FunderRow[]>([emptyFunder(1)]);
  const [nextId, setNextId] = useState(2);
  const [style, setStyle] = useState<FundingStyle>("elsevier");
  const [role, setRole] = useState<FunderRole>("none");
  const [roleText, setRoleText] = useState("");

  const outputLocale = strings.statementLocale;
  const funding = fundingSentence(funders, strings, {
    style,
    locale: outputLocale,
    grantLabel: (count) => tPlural(strings.grantNumber, locale, count).trim(),
  });
  const count = namedFunders(funders).length;
  const roleSentence = (() => {
    if (role === "described") {
      return asSentence(roleText);
    }
    if (role === "omit") {
      return "";
    }
    const template =
      style === "elsevier"
        ? strings.roleNoneElsevier
        : strings.roleNoneNarrative;
    return tPlural(template, locale, count).trim();
  })();
  const noFunding =
    style === "elsevier"
      ? strings.noFundingElsevier
      : strings.noFundingNarrative;
  const statement = funded
    ? [funding, funding === "" ? "" : roleSentence]
        .filter((text) => text !== "")
        .join(" ")
    : noFunding;
  const notes = funded ? noticesFor(funders, style, strings) : [];

  function loadExample() {
    setFunded(true);
    setFunders(
      strings.exampleFunders.map((name, index) => ({
        id: nextId + index,
        name,
        location: strings.exampleLocations[index] ?? "",
        grants: strings.exampleGrants[index] ?? "",
        recipients: strings.exampleRecipients[index] ?? "",
        url: strings.exampleUrls[index] ?? "",
      }))
    );
    setNextId((id) => id + strings.exampleFunders.length);
    setRole("none");
  }

  const styleOptions: Option<FundingStyle>[] = [
    { value: "elsevier", label: strings.styleElsevier },
    { value: "narrative", label: strings.styleNarrative },
  ];
  const roleOptions: Option<FunderRole>[] = [
    { value: "none", label: strings.roleNone },
    { value: "described", label: strings.roleDescribed },
    { value: "omit", label: strings.roleOmit },
  ];

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.fundedLabel}
        name="fst-funded"
        onChange={(value) => setFunded(value === "yes")}
        options={[
          { value: "yes", label: strings.fundedYes },
          { value: "no", label: strings.fundedNo },
        ]}
        value={funded ? "yes" : "no"}
      />
      {funded ? (
        <div className="space-y-4">
          {funders.map((row, index) => (
            <FunderFields
              canRemove={funders.length > 1}
              index={index}
              key={row.id}
              onChange={(patch) =>
                setFunders((current) =>
                  current.map((item) =>
                    item.id === row.id ? { ...item, ...patch } : item
                  )
                )
              }
              onRemove={() =>
                setFunders((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
            />
          ))}
          <SmallButton
            disabled={funders.length >= MAX_FUNDERS}
            label={strings.addFunder}
            onClick={() => {
              setFunders((current) => [...current, emptyFunder(nextId)]);
              setNextId((id) => id + 1);
            }}
          />
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.styleLabel}
          name="fst-style"
          onChange={setStyle}
          options={styleOptions}
          value={style}
        />
        {funded ? (
          <SelectField<FunderRole>
            id="fst-role"
            label={strings.roleLabel}
            onChange={setRole}
            options={roleOptions}
            value={role}
          />
        ) : null}
      </div>
      {funded && role === "described" ? (
        <TextAreaField
          id="fst-role-text"
          label={strings.roleTextLabel}
          onChange={setRoleText}
          placeholder={strings.roleTextPlaceholder}
          prose
          rows={3}
          value={roleText}
        />
      ) : null}
      <ExampleButton label={strings.tryExample} onClick={loadExample} />

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {statement === "" ? (
          <Notice>{strings.prompt}</Notice>
        ) : (
          <StatementCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            heading={strings.heading}
            paragraphs={[plainParagraph(statement)]}
          />
        )}
        <NoticeList items={notes} />
      </section>
    </div>
  );
}
